// =======================================================
// ThinkingStream — Claude Code "thinking" indicator, scaled
// =======================================================
//
// One clean status line, the way an AI editor shows it while it
// chews on a prompt:
//
//   ✻ Wintercircusing…  (12s · ↑ 1.4k tokens · esc to interrupt)
//
// The star glyph cycles. The gerund rolls vertically (slot-machine
// style) every couple of seconds. A faint "history" of past verbs
// fades upward so the 540px height isn't empty above the line.

import * as THREE from 'three';
import { Generator } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform sampler2D uMap;
  uniform vec3 uBg;
  void main() {
    vec4 t = texture2D(uMap, vUv);
    // canvas is drawn with premultiplied colors on transparent bg, so we
    // composite over uBg using t.a as the coverage.
    gl_FragColor = vec4(mix(uBg, t.rgb / max(t.a, 0.001), t.a), 1.0);
  }
`;

const WORDS = [
  'Gathering', 'AIDaying', 'TechWolfing', 'Wintercircusing', 'Networking',
  'Compiling', 'Embedding', 'Indexing', 'Inferring', 'Tokenizing',
  'Sampling', 'Streaming', 'Hosting', 'Designing', 'Brewing',
  'Prompting', 'Caching', 'Routing', 'Booting', 'Loading',
  'Scraping', 'Linking', 'Quantizing', 'Rendering', 'Pairing',
  'Vibing', 'Shipping', 'Synthesizing', 'Encoding', 'Decoding',
  'Drafting', 'Reviewing', 'Merging', 'Branching', 'Pushing',
  'Pulling', 'Curating', 'Mapping', 'Ghentifying', 'Coffeeing',
  'Demoing', 'Pitching', 'Hacking', 'Building', 'Sketching',
  'Refactoring', 'Spacemaking', 'Hydrating', 'Bundling', 'Diffing',
  'Hashing', 'Linting', 'Tracing', 'Profiling', 'Migrating',
  'Patching', 'Resolving', 'Awaiting', 'Forking', 'Cloning',
  'Stitching', 'Querying', 'Aligning', 'Reasoning', 'Reflecting',
  'Imagining', 'Plotting', 'Prototyping', 'Wiring', 'Tuning',
];

const DEFAULT_WORD_LIST = WORDS.join(', ');
const GLYPHS = ['✻', '✶', '✦', '✺', '✳', '✷'];

function positiveMod(n, d) {
  return ((n % d) + d) % d;
}

export class ThinkingStream extends Generator {
  constructor() {
    super({
      name: 'Thinking Stream',
      category: 'ambient',
      schema: {
        fontPx:        { default: 220, min: 40,  max: 480, step: 2,    label: 'Font size (px)' },
        metaScale:     { default: 0.42, min: 0.2, max: 0.9, step: 0.01, label: 'Meta size ratio' },
        rollEvery:     { default: 2.4, min: 0.4, max: 8,   step: 0.05, label: 'Word roll (s)' },
        rollDur:       { default: 0.55, min: 0.1, max: 2,  step: 0.01, label: 'Roll duration (s)' },
        words:         { default: DEFAULT_WORD_LIST, label: 'Words' },
        glyphHz:       { default: 6,   min: 1,   max: 20,  step: 0.5,  label: 'Glyph spin Hz' },
        backStream:    { default: true, type: 'bool', label: 'Back word stream' },
        backRows:      { default: 5,   min: 0,   max: 14,  step: 1,    label: 'Back rows' },
        backSize:      { default: 0.32, min: 0.08, max: 1.2, step: 0.01, label: 'Back text size' },
        backIntensity: { default: 0.12, min: 0,  max: 0.8, step: 0.01, label: 'Back intensity' },
        backSideSpeed: { default: 1.0, min: -3,  max: 3,   step: 0.05, label: 'Back side speed' },
        backUpSpeed:   { default: 1.0, min: -3,  max: 3,   step: 0.05, label: 'Back up speed' },
        traceLines:    { default: 6,   min: 0,   max: 20,  step: 1,    label: 'History lines' },
        traceFade:     { default: 0.6, min: 0,   max: 1,   step: 0.01, label: 'History fade' },
        traceGap:      { default: 1.4, min: 1.0, max: 3.0, step: 0.05, label: 'History line gap' },
        showMeta:      { default: true, type: 'bool', label: 'Show meta tail' },
        showCaret:     { default: true, type: 'bool', label: 'Blinking caret' },
        align:         { default: 'center', type: 'select', label: 'Align',
                         options: [['left','Left'],['center','Center'],['right','Right']] },
        textColor:     { default: '#e8eef5', type: 'color', label: 'Text' },
        metaColor:     { default: '#6b7686', type: 'color', label: 'Meta text' },
        backColor:     { default: '#445064', type: 'color', label: 'Back text' },
        glyphColor:    { default: '#c8a4ff', type: 'color', label: 'Glyph' },
        background:    { default: '#0a0d12', type: 'color', label: 'Background' },
      }
    });
    this._t0 = 0;
    this._tokens = 0;
    this._wordIdx = 0;
    this._prevWordIdx = 0;
    this._lastRoll = this._t0;
    this._history = []; // { word, t } — recently retired verbs
  }

  _words() {
    const parsed = String(this.v('words') || '')
      .split(/[\n,|]+/)
      .map(word => word.trim())
      .filter(Boolean);
    return parsed.length ? parsed : WORDS;
  }

  _clampWordIndexes(words = this._words()) {
    const max = Math.max(0, words.length - 1);
    this._wordIdx = Math.min(this._wordIdx, max);
    this._prevWordIdx = Math.min(this._prevWordIdx, max);
  }

  onEnter(scene, engine) {
    this.engine = engine;

    this.off = document.createElement('canvas');
    this.offCtx = this.off.getContext('2d', { alpha: true });
    this._resizeCanvas(engine.width, engine.height);

    this.tex = new THREE.CanvasTexture(this.off);
    this.tex.minFilter = THREE.LinearFilter;
    this.tex.magFilter = THREE.LinearFilter;
    this.tex.generateMipmaps = false;

    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uMap: { value: this.tex },
        uBg:  { value: new THREE.Color(this.v('background')) },
      },
      transparent: false,
      depthWrite: false,
      depthTest: false
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
    this.track(this.tex);

    this._t0 = engine.mediaTime ?? engine.time ?? 0;
    const words = this._words();
    this._wordIdx = Math.floor(Math.random() * words.length);
    this._prevWordIdx = this._wordIdx;
    this._lastRoll = 0;
    this._tokens = 0;
    this._history = [];
  }

  onResize(w, h) { this._resizeCanvas(w, h); }

  onParamChange(key, val) {
    if (!this.mesh) return;
    if (key === 'background') this.mesh.material.uniforms.uBg.value.set(val);
    if (key === 'words') {
      this._clampWordIndexes();
      this._history = [];
    }
  }

  reseed() {
    const words = this._words();
    this._wordIdx = Math.floor(Math.random() * words.length);
    this._prevWordIdx = this._wordIdx;
    this._tokens = 0;
    this._t0 = this.engine?.mediaTime ?? this.engine?.time ?? 0;
    this._lastRoll = this._t0;
    this._history = [];
  }

  onMediaTimeReset(time = 0) {
    this._t0 = time;
    this._lastRoll = time;
    this._tokens = 0;
    this._history = [];
  }

  _resizeCanvas(w, h) {
    const maxW = 4096;
    const maxH = 1080;
    this.off.width  = Math.max(2, Math.min(maxW, Math.round(w)));
    this.off.height = Math.max(2, Math.min(maxH, Math.round(h)));
    this._scale = this.off.width / Math.max(1, w);
  }

  _rgba(c, a) {
    let r = 255, g = 255, b = 255;
    if (typeof c === 'string') {
      const h = c.replace('#', '');
      if (h.length >= 6) {
        r = parseInt(h.substring(0, 2), 16);
        g = parseInt(h.substring(2, 4), 16);
        b = parseInt(h.substring(4, 6), 16);
      } else if (h.length >= 3) {
        r = parseInt(h[0] + h[0], 16);
        g = parseInt(h[1] + h[1], 16);
        b = parseInt(h[2] + h[2], 16);
      }
    } else if (c && typeof c === 'object') {
      r = Math.round(c.r ?? 255);
      g = Math.round(c.g ?? 255);
      b = Math.round(c.b ?? 255);
    }
    return `rgba(${r},${g},${b},${a})`;
  }

  // ease-out cubic
  _ease(t) { const u = 1 - t; return 1 - u * u * u; }

  _drawBackStream(ctx, words, time, w, h, fontPx, metaPx, baselineY) {
    const rowCount = Math.floor(this.v('backRows'));
    const intensity = this.v('backIntensity');
    if (!this.v('backStream') || rowCount <= 0 || intensity <= 0) return;

    ctx.save();
    const backPx = Math.max(6, fontPx * this.v('backSize'));
    ctx.font = `400 ${backPx}px 'Pumpa', 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';

    const rowGap = Math.max(backPx * 1.9, h / Math.max(3, rowCount + 1));
    const tileW = Math.max(backPx * 5.5, w / 8);
    const sideSpeed = this.v('backSideSpeed');
    const upSpeed = this.v('backUpSpeed');
    const yTravel = rowGap * Math.max(1, rowCount);
    const yOffset = ((time * upSpeed * 18 * this._scale) % rowGap + rowGap) % rowGap;

    for (let row = -1; row <= rowCount; row++) {
      const baseY = baselineY - yTravel * 0.5 + row * rowGap;
      const y = baseY - yOffset;
      if (Math.abs(y - baselineY) < fontPx * 0.42) continue;
      if (y < -rowGap || y > h + rowGap) continue;

      const direction = row % 2 === 0 ? 1 : -1;
      const speed = direction * sideSpeed * (10 + Math.abs(row) * 3) * this._scale;
      const offset = ((time * speed + row * tileW * 0.37) % tileW + tileW) % tileW;
      const pulse = 0.55 + 0.45 * Math.sin(time * 0.55 + row * 1.7);
      const centerFade = Math.min(1, Math.abs(y - baselineY) / Math.max(fontPx * 0.85, h * 0.08));
      const edgeFade = Math.min(1, y / rowGap, (h - y) / rowGap);
      const alpha = intensity * (0.45 + pulse * 0.35) * Math.max(0, edgeFade) * (0.35 + centerFade * 0.65);
      ctx.fillStyle = this._rgba(this.v('backColor'), alpha);

      for (let x = -tileW - offset; x < w + tileW; x += tileW) {
        const wordA = words[positiveMod(row * 7 + Math.floor((x + offset) / tileW) * 3, words.length)];
        const wordB = words[positiveMod(row * 11 + Math.floor((x + offset) / tileW) * 5 + 3, words.length)];
        ctx.fillText(`${wordA} ... ${wordB}`, x, y);
      }
    }

    ctx.restore();
  }

  update(dt, time, engine) {
    const ctx = this.offCtx;
    const w = this.off.width;
    const h = this.off.height;
    ctx.clearRect(0, 0, w, h);

    const now = engine.mediaTime ?? time;
    const elapsed = now - this._t0;

    // accumulate fake token count — climbs fast at first, slows down,
    // resets when a new verb rolls in (feels like a fresh "thinking" round)
    this._tokens += dt * (180 + Math.sin(now * 0.7) * 60);

    // word roll cadence
    const rollEvery = this.v('rollEvery');
    const rollDur = Math.min(this.v('rollDur'), rollEvery * 0.9);
    const words = this._words();
    this._clampWordIndexes(words);
    if (now - this._lastRoll >= rollEvery) {
      this._prevWordIdx = this._wordIdx;
      // pick a different word
      let next;
      do { next = Math.floor(Math.random() * words.length); }
      while (next === this._wordIdx && words.length > 1);
      this._wordIdx = next;
      this._lastRoll = now;
      this._history.unshift({ word: words[this._prevWordIdx], t: now });
      const maxHist = Math.floor(this.v('traceLines')) + 4;
      if (this._history.length > maxHist) this._history.length = maxHist;
      // gentle token jolt on each new round
      this._tokens += 30 + Math.random() * 80;
    }

    const sinceRoll = now - this._lastRoll;
    const rollT = Math.max(0, Math.min(1, sinceRoll / rollDur));
    const rolling = rollT < 1;
    const rollEase = this._ease(rollT);

    // ---- font sizes scaled to canvas ----
    const fontPx = Math.max(12, this.v('fontPx') * this._scale);
    const metaPx = Math.max(8, fontPx * this.v('metaScale'));

    const fontMain = `400 ${fontPx}px 'Pumpa', 'JetBrains Mono', ui-monospace, monospace`;
    const fontMeta = `200 ${metaPx}px 'JetBrains Mono', ui-monospace, monospace`;
    const baselineY = h / 2;

    this._drawBackStream(ctx, words, now, w, h, fontPx, metaPx, baselineY);

    // ---- composed line layout ----
    const glyphIdx = Math.floor(now * this.v('glyphHz')) % GLYPHS.length;
    const glyph = GLYPHS[glyphIdx];

    const wordCur = words[this._wordIdx];
    const wordPrev = words[this._prevWordIdx];
    // widest word for layout stability so the meta tail doesn't jiggle
    ctx.font = fontMain;
    const widestWord = words.reduce((acc, wd) => {
      const wW = ctx.measureText(wd).width;
      return wW > acc ? wW : acc;
    }, 0);
    const ellipsis = '…';
    const ellipsisW = ctx.measureText(ellipsis).width;
    const glyphW = GLYPHS.reduce((acc, g) => {
      const gW = ctx.measureText(g + '  ').width;
      return gW > acc ? gW : acc;
    }, 0);

    const metaText = this.v('showMeta')
      ? `  (${Math.floor(elapsed)}s · ↑ ${this._fmtTokens(this._tokens)} tokens · esc to interrupt)`
      : '';
    ctx.font = fontMeta;
    const metaW = ctx.measureText(metaText).width;

    const totalW = glyphW + widestWord + ellipsisW + metaW;
    const align = this.v('align');
    let x = (w - totalW) / 2;
    const margin = fontPx * 0.4;
    if (align === 'left')  x = margin;
    if (align === 'right') x = w - totalW - margin;

    // ---- glyph (rotating star) ----
    ctx.save();
    ctx.font = fontMain;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    // soft pulse on the glyph
    const glyphPulse = 0.85 + 0.15 * Math.sin(now * 4.0);
    ctx.fillStyle = this._rgba(this.v('glyphColor'), glyphPulse);
    ctx.fillText(glyph, x, baselineY);
    ctx.restore();

    const wordX = x + glyphW;

    // ---- word slot-machine ----
    // Outgoing verb slides up & fades; incoming slides up from below.
    ctx.save();
    ctx.font = fontMain;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';

    // clip to a tight band around the line so the rolling words don't
    // bleed into the history trace above.
    const bandH = fontPx * 1.15;
    ctx.beginPath();
    ctx.rect(wordX - 8, baselineY - bandH / 2, widestWord + 16, bandH);
    ctx.clip();

    if (rolling) {
      const slide = fontPx * 1.05;
      // outgoing
      const outAlpha = 1 - rollEase;
      ctx.fillStyle = this._rgba(this.v('textColor'), outAlpha);
      ctx.fillText(wordPrev, wordX, baselineY - slide * rollEase);
      // incoming
      const inAlpha = rollEase;
      ctx.fillStyle = this._rgba(this.v('textColor'), inAlpha);
      ctx.fillText(wordCur, wordX, baselineY + slide * (1 - rollEase));
    } else {
      ctx.fillStyle = this._rgba(this.v('textColor'), 1);
      ctx.fillText(wordCur, wordX, baselineY);
    }
    ctx.restore();

    // ---- ellipsis (animates dot count) ----
    ctx.save();
    ctx.font = fontMain;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.fillStyle = this._rgba(this.v('textColor'), 0.85);
    ctx.fillText(ellipsis, wordX + widestWord, baselineY);
    ctx.restore();

    // ---- meta tail ----
    if (this.v('showMeta')) {
      ctx.save();
      ctx.font = fontMeta;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      ctx.fillStyle = this._rgba(this.v('metaColor'), 0.9);
      ctx.fillText(metaText, wordX + widestWord + ellipsisW, baselineY);
      ctx.restore();
    }

    // ---- blinking caret at end of line ----
    if (this.v('showCaret')) {
      const caretBlink = (Math.floor(now * 1.6) % 2) === 0;
      if (caretBlink) {
        const caretX = wordX + widestWord + ellipsisW + metaW + fontPx * 0.12;
        const caretH = metaPx * 1.2;
        ctx.fillStyle = this._rgba(this.v('metaColor'), 0.7);
        ctx.fillRect(caretX, baselineY - caretH / 2, Math.max(2, metaPx * 0.18), caretH);
      }
    }

    // ---- history trace (faint past verbs floating up) ----
    const traceN = Math.floor(this.v('traceLines'));
    if (traceN > 0) {
      ctx.save();
      ctx.font = fontMeta;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      const lineGap = metaPx * this.v('traceGap');
      const fade = this.v('traceFade');
      for (let i = 0; i < Math.min(traceN, this._history.length); i++) {
        const item = this._history[i];
        const age = now - item.t;
        // each entry drifts upward & fades
        const driftY = baselineY - bandH * 0.7 - i * lineGap - Math.min(lineGap, age * 4);
        if (driftY < -lineGap) continue;
        const lifeFade = Math.max(0, 1 - (i / traceN));
        const a = lifeFade * (1 - fade * 0.4);
        const text = `${GLYPHS[(glyphIdx + i + 1) % GLYPHS.length]}  ${item.word}…`;
        ctx.fillStyle = this._rgba(this.v('metaColor'), a * 0.55);
        ctx.fillText(text, wordX, driftY);
      }
      ctx.restore();
    }

    this.tex.needsUpdate = true;
  }

  _fmtTokens(n) {
    const k = n / 1000;
    if (k >= 1) return `${k.toFixed(1)}k`;
    return String(Math.floor(n));
  }
}
